import { useEffect, useId, useState } from 'react';

import {
  approveIdea,
  clearUsageLimitHold,
  type CostEstimate,
  contentPath,
  createPost,
  fetchContentPlan,
  markContentReady,
  markPostPosted,
  requestChanges,
  type PlanProblem,
  POST_PLATFORMS,
  type Post,
  type PostPlatform,
  setPlaced,
  unpostContent,
  unschedulePosts,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError } from '../data.tsx';
import { fromLocalInputValue, toLocalInputValue } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import { Facts } from '../ui.tsx';
import * as wording from '../wording.ts';
import { type ActionDialog, startProductionOf } from './actions.ts';
import { localMoment, postPlatformLabel } from './lines.ts';
import { draftLabelMissing, newPostDraft, type PostDraft, PostFields, postInputOf } from './PostForm.tsx';
import { type BoardCard, cardTitle, type ContentCard, type IdeaCard } from './model.ts';
import { defaultPostFormat, defaultPostPlatform } from './posting.ts';

/**
 * The dialogs a card's primary action opens (US-019, Design reference
 * § Dialogs), each on `ConfirmDialog`. The board page mounts the one that is
 * open, so every opening starts from its defaults. A dialog that worked shows
 * `toast.ok`, calls `onDone` (which reads the board and the app data again)
 * and closes; a refusal it can explain stays in the dialog, anything else is
 * a `toast.error`.
 */

interface DialogProps<C extends BoardCard> {
  readonly card: C;
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}

/** The open dialog, for the board page to draw: `null` draws nothing. */
export function ActionDialogs({
  open,
  onClose,
  onDone,
}: {
  readonly open: { readonly dialog: ActionDialog; readonly card: BoardCard; readonly day?: string } | null;
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}) {
  if (open === null) return null;
  const { dialog, card } = open;
  if (card.type === 'idea') {
    return dialog === 'approve-idea' ? <ApproveIdeaDialog card={card} onClose={onClose} onDone={onDone} /> : null;
  }
  switch (dialog) {
    case 'mark-ready':
      return <MarkReadyDialog content={card.content} onClose={onClose} onDone={onDone} />;
    case 'schedule':
      return (
        <ScheduleDialog
          card={card}
          onClose={onClose}
          onDone={onDone}
          {...(open.day === undefined ? {} : { day: open.day })}
        />
      );
    case 'mark-posted':
      return <MarkPostedDialog card={card} onClose={onClose} onDone={onDone} />;
    case 'resume-hold':
      return <ResumeHoldDialog card={card} onClose={onClose} onDone={onDone} />;
    case 'request-changes':
      return <RequestChangesDialog card={card} onClose={onClose} onDone={onDone} />;
    case 'unschedule':
      return <UnscheduleDialog card={card} onClose={onClose} onDone={onDone} />;
    case 'undo-posting':
      return <UndoPostingDialog card={card} onClose={onClose} onDone={onDone} />;
    default:
      return null;
  }
}

/** Runs `work` with the dialog busy; a throw is shown as `toast.error` and leaves it open. */
function useBusy(): [boolean, (work: () => Promise<void>) => Promise<void>] {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await work();
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

/** "Approve “{title}”?": the idea becomes a content, and its planning starts on its own. */
export function ApproveIdeaDialog({ card, onClose, onDone }: DialogProps<IdeaCard>) {
  const toast = useToast();
  const [busy, run] = useBusy();
  const title = cardTitle(card);

  const confirm = (): Promise<void> =>
    run(async () => {
      const approval = await approveIdea(card.batch.id, card.idea.id);
      toast.ok(wording.contentBeingPlanned(approval.idea.content?.name ?? title), {
        href: contentPath(approval.contentId),
      });
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.APPROVE_IDEA_TITLE}
      confirmLabel={wording.ACTION_APPROVE_IDEA}
      busyLabel={wording.APPROVING}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <p>{wording.approveIdeaBody(title)}</p>
    </ConfirmDialog>
  );
}

/**
 * The plan's estimated cost against the spend cap, and **Mark ready**; with
 * **Start production straight away** ticked (the default) production starts
 * right after. A plan that does not parse lists its problems and stays open.
 * Takes the content rather than its card, so the content page's planning
 * conversation (US-023) opens the same dialog.
 */
export function MarkReadyDialog({
  content,
  onClose,
  onDone,
}: {
  readonly content: { readonly id: string; readonly name: string };
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const checkboxId = useId();
  const [busy, run] = useBusy();
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [start, setStart] = useState(true);
  const [problems, setProblems] = useState<PlanProblem[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchContentPlan(content.id, controller.signal)
      .then((plan) => {
        setEstimate(plan.estimate);
      })
      .catch(() => {
        // Without the figure the dialog still marks ready; the content page has the estimate.
      });
    return () => {
      controller.abort();
    };
  }, [content.id]);

  const confirm = (): Promise<void> =>
    run(async () => {
      const result = await markContentReady(content.id);
      if (!result.ok) {
        setProblems(result.errors);
        return;
      }
      if (!start) {
        toast.ok(wording.markedReady(content.name));
        onClose();
        await onDone();
        return;
      }
      try {
        const started = await startProductionOf(content.id, content.name);
        if (started.kind === 'info') toast.info(started.text);
        else toast.ok(started.text);
      } catch (cause: unknown) {
        // Ready it is; only the start was refused.
        toast.error(describeError(cause));
      }
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.markReadyTitle(content.name)}
      confirmLabel={wording.ACTION_MARK_READY}
      busyLabel={wording.MARK_READY_BUSY}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      {estimate === null ? (
        <p className="muted">{wording.MARK_READY_READING}</p>
      ) : (
        <Facts
          items={[
            { label: wording.MARK_READY_ESTIMATE, value: wording.dollars(estimate.usd) },
            {
              label: wording.MARK_READY_CAP,
              value: estimate.capUsd === null ? wording.MARK_READY_NO_CAP : wording.dollars(estimate.capUsd),
            },
          ]}
        />
      )}
      <label className="checkbox" htmlFor={checkboxId}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={start}
          onChange={(event) => {
            setStart(event.target.checked);
          }}
        />
        {wording.MARK_READY_START}
      </label>
      {problems !== null && (
        <div role="alert">
          <p className="field__hint field__hint--error">{wording.markReadyProblems(problems.length)}</p>
          <ul>
            {problems.map((problem, index) => (
              <li key={`${String(problem.line)}-${String(index)}`}>
                {problem.line > 0 ? `Line ${String(problem.line)}: ` : ''}
                {problem.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </ConfirmDialog>
  );
}

/**
 * Plans a post of a delivered content: platform, paid or not, a label (Other
 * needs one), the format, when, and notes, through `POST /api/contents/:id/posts`.
 * `day` is the calendar day a card was dropped on (US-033). The fields are
 * {@link PostFields}, which the posts panel (US-024) draws inline too.
 */
export function ScheduleDialog({ card, onClose, onDone, day }: DialogProps<ContentCard> & { readonly day?: string }) {
  const toast = useToast();
  const content = card.content;
  const [busy, run] = useBusy();
  const [draft, setDraft] = useState<PostDraft>(() => newPostDraft(content, card.posts, day));
  const [error, setError] = useState<string | null>(null);

  const confirm = (): Promise<void> =>
    run(async () => {
      setError(null);
      const input = postInputOf(draft);
      try {
        await createPost(content.id, input);
      } catch (cause: unknown) {
        // 400 `invalid_post` names the field; the form stays as typed.
        setError(describeError(cause));
        return;
      }
      toast.ok(wording.scheduled(content.name, postPlatformLabel(draft.platform, input.label ?? null)));
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.SCHEDULE_TITLE}
      confirmLabel={wording.SCHEDULE_CONFIRM}
      busyLabel={wording.SCHEDULE_BUSY}
      busy={busy}
      confirmDisabled={draftLabelMissing(draft)}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <PostFields draft={draft} formats={content.formats} onChange={setDraft} />
      {error !== null && (
        <p className="field__hint field__hint--error" role="alert">
          {error}
        </p>
      )}
    </ConfirmDialog>
  );
}

/**
 * Records that the content went out. With planned posts: one checkbox each
 * (the due ones ticked, else all), each with its link, and one "Posted at".
 * Without: the platform, Paid ad and the link of the post that is made and
 * marked at once. **Just mark it placed** skips the posts altogether.
 */
export function MarkPostedDialog({ card, onClose, onDone }: DialogProps<ContentCard>) {
  const toast = useToast();
  const id = useId();
  const content = card.content;
  const [busy, run] = useBusy();
  const [planned] = useState<readonly Post[]>(() => card.posts.filter((post) => post.status === 'planned'));
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => {
    const now = Date.now();
    const due = planned.filter((post) => post.scheduledAt !== null && Date.parse(post.scheduledAt) <= now);
    return new Set((due.length > 0 ? due : planned).map((post) => post.id));
  });
  const [urls, setUrls] = useState<Readonly<Record<string, string>>>({});
  const [postedAt, setPostedAt] = useState(() => toLocalInputValue(new Date().toISOString()));
  const [platform, setPlatform] = useState<PostPlatform>(() => defaultPostPlatform(content, card.posts));
  const [paid, setPaid] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const url = (key: string): string | null => {
    const value = (urls[key] ?? '').trim();
    return value === '' ? null : value;
  };

  const finish = async (text: string): Promise<void> => {
    toast.ok(text);
    onClose();
    await onDone();
  };

  const confirm = (): Promise<void> =>
    run(async () => {
      setError(null);
      const at = fromLocalInputValue(postedAt);
      const when = at === null ? {} : { postedAt: at };
      if (planned.length === 0) {
        let post: Post;
        try {
          post = await createPost(content.id, {
            platform,
            paid,
            label: label.trim() === '' ? null : label.trim(),
            format: defaultPostFormat(platform, content.formats),
          });
        } catch (cause: unknown) {
          setError(describeError(cause));
          return;
        }
        await markPostPosted(post.id, { ...when, url: url('new') });
        await finish(wording.markedPosted(content.name));
        return;
      }
      // One at a time: a refusal half-way leaves the ones before it posted, which the re-read shows.
      try {
        for (const post of planned) {
          if (ticked.has(post.id)) await markPostPosted(post.id, { ...when, url: url(post.id) });
        }
      } catch (cause: unknown) {
        await onDone();
        throw cause;
      }
      await finish(wording.markedPosted(content.name));
    });

  const placeOnly = (): void => {
    void run(async () => {
      await setPlaced(content.id, true);
      await finish(wording.markedPlaced(content.name));
    });
  };

  return (
    <ConfirmDialog
      open
      title={wording.MARK_POSTED_TITLE}
      confirmLabel={wording.MARK_POSTED_CONFIRM}
      busyLabel={wording.MARK_POSTED_BUSY}
      busy={busy}
      confirmDisabled={planned.length === 0 ? platform === 'other' && label.trim() === '' : ticked.size === 0}
      alternative={{ label: wording.MARK_POSTED_JUST_PLACED, onClick: placeOnly }}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      {planned.length > 0 ? (
        <fieldset className="mark-posted__posts">
          <legend className="field__label">{wording.MARK_POSTED_PICK}</legend>
          {planned.map((post) => (
            <div key={post.id} className="mark-posted__post">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={ticked.has(post.id)}
                  onChange={(event) => {
                    const next = new Set(ticked);
                    if (event.target.checked) next.add(post.id);
                    else next.delete(post.id);
                    setTicked(next);
                  }}
                />
                {postPlatformLabel(post.platform, post.label)}
                {post.paid ? ` · ${wording.POST_PAID}` : ''}
                {post.scheduledAt === null ? '' : ` · ${localMoment(post.scheduledAt)}`}
              </label>
              <input
                className="field__input"
                type="url"
                aria-label={`${wording.POST_URL}: ${postPlatformLabel(post.platform, post.label)}`}
                placeholder="https://"
                disabled={!ticked.has(post.id)}
                value={urls[post.id] ?? ''}
                onChange={(event) => {
                  setUrls({ ...urls, [post.id]: event.target.value });
                }}
              />
            </div>
          ))}
        </fieldset>
      ) : (
        <>
          <p>{wording.MARK_POSTED_NONE}</p>
          <div className="field">
            <label className="field__label" htmlFor={`${id}-platform`}>
              {wording.POST_PLATFORM}
            </label>
            <select
              id={`${id}-platform`}
              className="field__input"
              value={platform}
              onChange={(event) => {
                setPlatform(event.target.value as PostPlatform);
              }}
            >
              {POST_PLATFORMS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {platform === 'other' && (
            <div className="field">
              <label className="field__label" htmlFor={`${id}-label`}>
                {wording.POST_LABEL}
              </label>
              <input
                id={`${id}-label`}
                className="field__input"
                type="text"
                maxLength={80}
                required
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value);
                }}
              />
            </div>
          )}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={paid}
              onChange={(event) => {
                setPaid(event.target.checked);
              }}
            />
            {wording.POST_PAID}
          </label>
          <div className="field">
            <label className="field__label" htmlFor={`${id}-url`}>
              {wording.POST_URL}
            </label>
            <input
              id={`${id}-url`}
              className="field__input"
              type="url"
              placeholder="https://"
              value={urls['new'] ?? ''}
              onChange={(event) => {
                setUrls({ ...urls, new: event.target.value });
              }}
            />
          </div>
        </>
      )}
      <div className="field">
        <label className="field__label" htmlFor={`${id}-at`}>
          {wording.MARK_POSTED_AT}
        </label>
        <input
          id={`${id}-at`}
          className="field__input"
          type="datetime-local"
          value={postedAt}
          onChange={(event) => {
            setPostedAt(event.target.value);
          }}
        />
      </div>
      {error !== null && (
        <p className="field__hint field__hint--error" role="alert">
          {error}
        </p>
      )}
    </ConfirmDialog>
  );
}

/**
 * **Resume now** on a held content: the content page's confirmation, since
 * the hold is the whole account's and lifting it resumes every held content.
 */
export function ResumeHoldDialog({ onClose, onDone }: DialogProps<ContentCard>) {
  const toast = useToast();
  const [busy, run] = useBusy();

  const confirm = (): Promise<void> =>
    run(async () => {
      const { resumed } = await clearUsageLimitHold();
      toast.ok(wording.holdLifted(resumed));
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.RESUME_HOLD_TITLE}
      confirmLabel={wording.RESUME_HOLD_CONFIRM}
      busyLabel={wording.RESUME_HOLD_BUSY}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <p>
        <strong>This resumes every held content, not only this one.</strong> The usage limit is on the
        Claude account the whole server shares, so there is one hold; lifting it starts as many held
        contents as the production-slot cap allows, with the rest going on the queue in the order they
        were held.
      </p>
      <p>
        If the limit has in fact not lifted, the first agent to ask is refused again and a fresh hour
        begins. Nothing is lost either way: a refused step costs no attempt.
      </p>
    </ConfirmDialog>
  );
}

/**
 * "What should change?": the feedback for one headless pass that amends the
 * plan, after which the next version is produced (`POST /api/contents/:id/revise`).
 * The text is required; Ctrl/Cmd+Enter sends it. A revision the server could
 * not make (the amended plan broke a rule) says why and stays open, with the
 * text still in it.
 */
export function RequestChangesDialog({ card, onClose, onDone }: DialogProps<ContentCard>) {
  const toast = useToast();
  const fieldId = useId();
  const content = card.content;
  const [busy, run] = useBusy();
  const [feedback, setFeedback] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const blank = feedback.trim() === '';

  const confirm = (): Promise<void> =>
    run(async () => {
      setRefusal(null);
      // Refused at once — no feedback, not in review, one already being
      // planned — and the reason stays in the dialog beside what was typed.
      let result: Awaited<ReturnType<typeof requestChanges>>;
      try {
        result = await requestChanges(content.id, feedback.trim());
      } catch (cause: unknown) {
        setRefusal(describeError(cause));
        return;
      }
      toast.ok(result.message === '' ? wording.boardChangesRequested(content.name, result.nextVersion) : result.message);
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.REVIEW_CHANGES_LABEL}
      confirmLabel={wording.REQUEST_CHANGES}
      busyLabel={wording.WORKING}
      busy={busy}
      confirmDisabled={blank}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <div className="field">
        <label className="field__label" htmlFor={fieldId}>
          {wording.boardRequestChangesFor(content.name, content.currentVersion)}
        </label>
        <textarea
          id={fieldId}
          className="field__textarea"
          rows={5}
          required
          // The text is the whole dialog: typing starts here, not on Cancel.
          autoFocus
          value={feedback}
          placeholder={wording.REVIEW_CHANGES_PLACEHOLDER}
          disabled={busy}
          onChange={(event) => {
            setFeedback(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
            event.preventDefault();
            if (!blank && !busy) void confirm();
          }}
        />
        <p className="field__hint">{wording.boardChangesHint(content.currentVersion)}</p>
        {refusal !== null && (
          <p className="field__hint field__hint--error" role="alert">
            {refusal}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}

/**
 * A Scheduled card dropped on To post (US-025): its planned posts lose their
 * time and stay planned (`POST /api/contents/:id/posts/unschedule`).
 */
export function UnscheduleDialog({ card, onClose, onDone }: DialogProps<ContentCard>) {
  const toast = useToast();
  const [busy, run] = useBusy();
  const content = card.content;
  const dated = card.posts.filter((post) => post.status === 'planned' && post.scheduledAt !== null).length;

  const confirm = (): Promise<void> =>
    run(async () => {
      await unschedulePosts(content.id);
      toast.ok(wording.unscheduled(content.name));
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.UNSCHEDULE_TITLE}
      confirmLabel={wording.UNSCHEDULE_CONFIRM}
      busyLabel={wording.UNSCHEDULE_BUSY}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <p>{wording.unscheduleBody(dated)}</p>
    </ConfirmDialog>
  );
}

/**
 * A Posted card dropped on To post (US-025): its posted posts go back to
 * planned and the content is no longer placed (`POST /api/contents/:id/unpost`).
 * A content placed without any post is only unplaced.
 */
export function UndoPostingDialog({ card, onClose, onDone }: DialogProps<ContentCard>) {
  const toast = useToast();
  const [busy, run] = useBusy();
  const content = card.content;
  const posted = card.posts.filter((post) => post.status === 'posted').length;

  const confirm = (): Promise<void> =>
    run(async () => {
      if (posted === 0) await setPlaced(content.id, false);
      else await unpostContent(content.id);
      toast.ok(wording.postingUndone(content.name));
      onClose();
      await onDone();
    });

  return (
    <ConfirmDialog
      open
      title={wording.UNDO_POSTING_TITLE}
      confirmLabel={wording.UNDO_POSTING_CONFIRM}
      busyLabel={wording.UNDO_POSTING_BUSY}
      busy={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      <p>{posted === 0 ? wording.UNDO_POSTING_PLACED_BODY : wording.undoPostingBody(posted)}</p>
    </ConfirmDialog>
  );
}
